import { useState } from "react"
import { useMutation } from "urql"

import { AssociateProductType } from "../../../types"
import { BatchAddProductsToEquivalencyGroupDocument } from "../graphql/associateProducts.mutation"

import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseAssociateProductsMutationParams {
  equivalencyGroupId: string
  selections: Record<AssociateProductType, Record<string, boolean>>
  associatedProductIds: number[]
  associatedCompetitorIds: string[]
  onSuccess?: () => void
  onClose: () => void
}

interface UseAssociateProductsMutationReturn {
  handleSave: () => Promise<void>
  isSubmitting: boolean
  canSave: boolean
}

/**
 * Hook for handling the GraphQL mutation to associate products with an equivalency group
 * Manages submission state and success/error notifications
 */
export const useAssociateProductsMutation = ({
  equivalencyGroupId,
  selections,
  associatedProductIds,
  associatedCompetitorIds,
  onSuccess,
  onClose,
}: UseAssociateProductsMutationParams): UseAssociateProductsMutationReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [, executeMutation] = useMutation(BatchAddProductsToEquivalencyGroupDocument)

  // Calculate new product IDs (exclude already-associated)
  const akrochemIds = Object.keys(selections[AssociateProductType.Akrochem])
    .filter((id) => selections[AssociateProductType.Akrochem][id])
    .map((id) => Number.parseInt(id, 10))
    .filter((id) => !associatedProductIds.includes(id))

  const competitorIds = Object.keys(selections[AssociateProductType.Competitor])
    .filter((id) => selections[AssociateProductType.Competitor][id])
    .filter((id) => !associatedCompetitorIds.includes(id))

  const canSave = (akrochemIds.length > 0 || competitorIds.length > 0) && !isSubmitting

  const handleSave = async () => {
    if (!canSave) return

    setIsSubmitting(true)

    try {
      const input = {
        equivalencyGroupId,
        productIds: akrochemIds,
        competitorProductIds: competitorIds,
      }

      const result = await executeMutation({ input })

      if (result.error) {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: "Failed to associate products. Please try again.",
        })
        return
      }

      const data = result.data?.equivalencyGroup?.batchAddProductsToEquivalencyGroup

      if (data?.__typename === "EquivalencyGroupFailure") {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: data.error.message,
        })
        return
      }

      if (data?.__typename === "BatchAddProductsToEquivalencyGroupSuccess") {
        notify({
          type: NotificationType.Success,
          silent: false,
          message: "Selected products successfully associated.",
        })
        onSuccess?.()
        onClose()
      }
    } catch {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: "An unexpected error occurred. Please try again.",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    handleSave,
    isSubmitting,
    canSave,
  }
}
